import { memo, useState } from 'react';
import { AcoesTriagem, BotaoCancelar, BotaoManter } from '@/components/kanban/triagem/AcoesTriagem';
import { formatarDocumento } from '@/components/kanban/triagem/segmentoTriagem';
import { ManterComNotaModal } from '@/components/kanban/ManterComNotaModal';
import { diasParadoNaFila } from '@/components/kanban/triagem/diasParadoNaFila';
import { Button } from '@/components/ui/button';
import { DollarSign, Loader2, CheckCircle } from 'lucide-react';
import { CopyOnClick } from '@/components/planilha/CopyOnClick';
import { CotacaoInputPopover } from '@/components/kanban/solar/CotacaoInputPopover';
import { usePainelPedido } from '@/contexts/PainelPedidoContext';
import { CancelarItemModal } from '@/components/pedido/CancelarItemModal';
import type { PedidoTriagemErroSolar } from './useTriagemErroSolar';
import { resumoProdutos } from '@/components/kanban/triagem/triagemPedidoUtils';
import { PrazoSlaBadge } from '@/components/kanban/PrazoSlaBadge';
import { TRIAGEM_BTN, TRIAGEM_BTN_PRIMARIO } from '@/components/kanban/triagem/triagemTabela';
import { separarAparelho, useFlagsFaturamento } from '@/components/kanban/triagem/flagsFaturamento';

interface TriagemErroSolarRowProps {
  pedido: PedidoTriagemErroSolar;
  /** O 1º da fila: só ele tem os botões liberados (CPF ordena, não trava). */
  isTopo: boolean;
  posicao: number;
  onMoverParaFinal: (itemId: string) => void;
  onRealizarInput: (itemId: string, numeroCotacao: string) => void;
  onMoverParaTTRecursos: (itemId: string) => void;
  /** Só este pedido está esperando o banco; os outros da fila seguem livres. */
  ocupada: boolean;
  isMovingToFinal: boolean;
  isRealizandoInput: boolean;
  isMovingToTTRecursos: boolean;
}


export const TriagemErroSolarRow = memo(function TriagemErroSolarRow({
  pedido,
  isTopo,
  posicao,
  onMoverParaFinal,
  onRealizarInput,
  onMoverParaTTRecursos,
  ocupada,
  isMovingToFinal,
  isRealizandoInput,
  isMovingToTTRecursos,
}: TriagemErroSolarRowProps) {
  const [manterModalOpen, setManterModalOpen] = useState(false);
  const [cancelarModalOpen, setCancelarModalOpen] = useState(false);
  /* A linha é o pedido (Douglas 24/10/2026): os botões mandam a 1ª linha e o
     hook leva todas as linhas do pedido nesta fila. */
  const item = pedido.itens[0];
  const linhas = pedido.itens.length;
  const { resumo, completo } = resumoProdutos(pedido.itens);
  const cotacaoAtual = pedido.cotacoes[0];
  const cnpjFormatado = formatarDocumento(pedido.documento || pedido.cnpj);
  const travaFila = !isTopo && pedido.tipo_pessoa !== 'PF';
  const isProcessing = ocupada || travaFila;
  /* VALOR é só o plano (Douglas 30/10/2026): o aparelho tem KPI próprio e
     não entra (OTMA mostrava R$ 3.818,97, o plano é R$ 119,97). */
  const flagsFat = useFlagsFaturamento();
  const { faturamento: valorPlano } = separarAparelho(pedido.itens, flagsFat, (i) => i.produto, (i) => i.subtotal || 0);

  const { open: openPedido } = usePainelPedido();

  const handleOpenPedido = (e: React.MouseEvent) => {
    e.stopPropagation();
    openPedido(item.pedido_id, 'visao360');
  };

  return (
    <tr className={isTopo
      ? 'box-border h-[33px] border-b border-border/30 bg-accent/40 shadow-[inset_3px_0_0_hsl(var(--primary))] transition-colors hover:bg-accent/70'
      : 'box-border h-[33px] border-b border-border/30 transition-colors hover:bg-muted/30'}
      title={travaFila ? 'FIFO: trabalhe o pedido do topo primeiro' : undefined}>
      <td className="px-2 py-0 align-middle">
        <span className={`text-[10px] font-bold tabular-nums ${isTopo ? 'text-primary' : 'text-muted-foreground'}`}>{posicao + 1}º</span>
      </td>
      <td className="px-2 py-0 align-middle">
        <CopyOnClick value={pedido.documento || pedido.cnpj} className="tabular-nums text-xs">
          {cnpjFormatado}
        </CopyOnClick>
      </td>

      <td className="px-2 py-0 align-middle">
        <span className="block truncate text-xs font-semibold text-foreground" title={pedido.razao_social}>
          {pedido.razao_social || '-'}
        </span>
      </td>

      <td className="px-2 py-0 align-middle">
        {pedido.cotacoes.length ? (
          <CopyOnClick value={pedido.cotacoes.join(' ')} className="block truncate text-xs tabular-nums">
            {pedido.cotacoes.join(' · ')}
          </CopyOnClick>
        ) : (
          <span className="text-[11px] text-primary dark:text-[#C87484]" title="O número da cotação entra no Input realizado e vale para todas as linhas do pedido">aguardando</span>
        )}
      </td>

      <td className="px-2 py-0 align-middle">
        <button
          onClick={handleOpenPedido}
          className="cursor-pointer text-xs font-bold tabular-nums text-primary hover:underline dark:text-[#C87484]"
          title="Abrir detalhes do pedido"
        >
          {pedido.numero_pedido}
        </button>
      </td>

      <td className="px-2 py-0 align-middle">
        <span className="block truncate text-xs text-muted-foreground" title={completo}>
          {resumo || '-'}
        </span>
      </td>

      <td className="px-2 py-0 align-middle">
        <span className="text-xs text-muted-foreground" title={linhas > 1 ? `${linhas} linhas no pedido` : undefined}>{pedido.quantidade || '-'}</span>
      </td>

      <td className="px-2 py-0 align-middle">
        <span className="text-xs text-muted-foreground">
          {valorPlano ? `R$ ${valorPlano.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
        </span>
      </td>

      <td className="px-2 py-0 align-middle">
        <PrazoSlaBadge
          pedidoId={pedido.pedido_id}
          coluna="ERRO SOLAR  INPUT MANUAL"
          entradaColunaEm={pedido.entrada_coluna_em}
          prazoHoras={2}
        />
      </td>

      {/* AÇÕES */}
      <td className="px-2 py-0 align-middle">
        <AcoesTriagem
          manter={
            <BotaoManter
              onClick={() => setManterModalOpen(true)}
              disabled={isProcessing}
              carregando={isMovingToFinal}
            />
          }
          avancar={
            <CotacaoInputPopover
              onConfirm={(numeroCotacao) => onRealizarInput(item.item_id, numeroCotacao)}
              isLoading={isRealizandoInput}
              cotacaoAtual={cotacaoAtual || undefined}
            >
              <Button
                size="icon"
                variant="ghost"
                className={TRIAGEM_BTN_PRIMARIO}
                title={`${cotacaoAtual ? `Input realizado — trocar a cotação ${cotacaoAtual} e mandar para Validação pendente` : 'Input realizado — informar a cotação e mandar para Validação pendente'}${linhas > 1 ? ` (o pedido inteiro, ${linhas} linhas)` : ''}`}
                disabled={isProcessing}
              >
                {isRealizandoInput ? <Loader2 className="animate-spin" /> : <CheckCircle />}
              </Button>
            </CotacaoInputPopover>
          }
          desvios={
            // (Douglas 29/08) o mesmo botão da triagem de Input realizado móvel:
            // cliente com débito muda de fila para buscar a fatura, sem pedir o valor.
            <Button
              size="icon"
              variant="ghost"
              className={TRIAGEM_BTN}
              title="(TT) Recursos inválidos — dívida interna: mandar para a fila de TT recursos inválidos"
              onClick={() => onMoverParaTTRecursos(item.item_id)}
              disabled={isProcessing}
            >
              {isMovingToTTRecursos ? <Loader2 className="animate-spin" /> : <DollarSign />}
            </Button>
          }
          cancelar={
            <BotaoCancelar
              title="Pedido cancelado — abrir o cancelamento do item"
              onClick={() => setCancelarModalOpen(true)}
              disabled={isProcessing}
            />
          }
        />
        <ManterComNotaModal
          open={manterModalOpen}
          onOpenChange={setManterModalOpen}
          pedidoId={item.pedido_id}
          clienteId=""
          onConfirm={() => onMoverParaFinal(item.item_id)}
          justificarFila={{
            itemId: item.item_id,
            coluna: 'ERRO SOLAR  INPUT MANUAL',
            numeroPedido: item.numero_pedido,
            razaoSocial: item.razao_social,
            justificativaAtual: null,
            diasParado: diasParadoNaFila(pedido.entrada_coluna_em),
          }}
        />
        <CancelarItemModal
          open={cancelarModalOpen}
          onOpenChange={setCancelarModalOpen}
          itemId={item.item_id}
          pedidoId={item.pedido_id}
          itemNome={resumo || pedido.numero_pedido}
        />
      </td>
    </tr>
  );
});
